import { useCallback, useRef, useState, type MutableRefObject } from 'react'
import type { ChangeEvent } from 'react'
import type { UseFormRegisterReturn } from 'react-hook-form'
import { cn } from '@lib/utils/helpers'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RESIDENT_DOCUMENT_FILE_ACCEPT } from '../../constants/residentConstants'

/**
 * Same integrated row as company `PaymentsTab` `PaymentFileUploadField`:
 * hidden native file input + “Choose file…” + trailing “Upload”.
 * When `onImmediateUpload` is set, selecting a file uploads immediately
 * (Angular `selectFile` → `uploadFile` → `submitForm('fileUpload')`).
 */
export function ResidentFileUploadField({
  disabled = false,
  registerReturn,
  multiple = false,
  className,
  'aria-label': ariaLabel,
  dataTestId,
  accept = RESIDENT_DOCUMENT_FILE_ACCEPT,
  uploading = false,
  onImmediateUpload,
}: {
  disabled?: boolean
  registerReturn: UseFormRegisterReturn
  multiple?: boolean
  className?: string
  'aria-label'?: string
  /** Root wrapper; choose/upload buttons use `{dataTestId}-choose` and `{dataTestId}-upload`. */
  dataTestId?: string
  accept?: string
  uploading?: boolean
  /** When set, files POST immediately after pick (no form Save required). */
  onImmediateUpload?: (files: File[]) => void | Promise<void>
}) {
  const { ref, onChange, onBlur, name, ...fieldRest } = registerReturn
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [chosenDisplayName, setChosenDisplayName] = useState('')
  const [pendingLargeFile, setPendingLargeFile] = useState<{
    fileList: File[]
    message: string
    changeEvent: ChangeEvent<HTMLInputElement>
  } | null>(null)

  const setRefs = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el
      if (typeof ref === 'function') {
        ref(el)
      } else if (ref && typeof ref === 'object' && 'current' in ref) {
        // eslint-disable-next-line react-hooks/immutability -- standard forwarded-ref merge: assigning the caller's ref.current is the intended React pattern
        ;(ref as MutableRefObject<HTMLInputElement | null>).current = el
      }
    },
    [ref],
  )

  const busy = disabled || uploading

  const openPicker = () => {
    if (!busy) inputRef.current?.click()
  }

  const processFileSelection = async (
    fileList: File[],
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    if (multiple) {
      setChosenDisplayName(
        fileList.length > 1 ? `${fileList.length} files selected` : fileList[0]?.name ?? '',
      )
    } else {
      setChosenDisplayName(fileList[0]?.name ?? '')
    }

    if (onImmediateUpload) {
      try {
        await onImmediateUpload(fileList)
        setChosenDisplayName('')
        if (inputRef.current) inputRef.current.value = ''
        // Keep RHF in sync with a cleared file input after immediate upload.
        onChange({
          target: { name, value: '', files: null },
          type: 'change',
        } as unknown as ChangeEvent<HTMLInputElement>)
      } catch {
        // Parent surfaces API errors; keep the chosen name so the user can retry.
      }
      return
    }

    onChange(e)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) {
      onChange(e)
      setChosenDisplayName('')
      return
    }

    const fileList = Array.from(files)
    let hasOver10Mb = false
    let largeFileText = ''
    const tenMb = 10 * 1024 * 1024
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > tenMb) {
        hasOver10Mb = true
        largeFileText += `\n${files[i].name} - ${(files[i].size / 1024 / 1024).toFixed(2)} MB`
      }
    }

    if (hasOver10Mb) {
      const confirmMessage =
        name === 'lease_agreement'
          ? 'WARNING: Your file is larger than 10MB.\n' +
            'We only need the first 2-3 pages of the lease that shows the named parties, start / end dates, and rent amount.\n' +
            'We request you try to compress the file so it takes less storage.\n' +
            'However, if you are not able to compress it further, please continue.\n' +
            'Do you want to continue?'
          : 'WARNING: Your file is larger than 10MB.\n' +
            'We request you try to compress the file so it takes less storage.\n' +
            'However, if you are not able to compress it further, please continue.\n' +
            'Do you want to continue?'

      setPendingLargeFile({
        fileList,
        message: confirmMessage,
        changeEvent: e,
      })
      return
    }

    void processFileSelection(fileList, e)
  }

  const handleConfirmLargeFile = () => {
    if (!pendingLargeFile) return
    const { fileList, changeEvent } = pendingLargeFile
    setPendingLargeFile(null)
    void processFileSelection(fileList, changeEvent)
  }

  const handleCancelLargeFile = () => {
    if (inputRef.current) inputRef.current.value = ''
    setChosenDisplayName('')
    setPendingLargeFile(null)
  }

  const showText = uploading
    ? 'Uploading…'
    : chosenDisplayName.trim() || 'Choose file...'

  return (
    <>
      <div
        className={cn(
          'flex min-h-[2.75rem] items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 p-1.5',
          className,
        )}
        data-testid={dataTestId}
      >
        <button
          type="button"
          disabled={busy}
          aria-label={ariaLabel}
          onClick={openPicker}
          data-testid={dataTestId ? `${dataTestId}-choose` : undefined}
          className={cn(
            'min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm transition',
            chosenDisplayName.trim() || uploading ? 'text-slate-700' : 'text-slate-400',
            'hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60',
          )}
        >
          {showText}
        </button>
        <input
          {...fieldRest}
          ref={setRefs}
          type="file"
          name={name}
          accept={accept}
          onBlur={onBlur}
          onChange={handleChange}
          multiple={multiple}
          className="sr-only"
          disabled={busy}
        />
        <button
          type="button"
          disabled={busy}
          onClick={openPicker}
          data-testid={dataTestId ? `${dataTestId}-upload` : undefined}
          className="shrink-0 rounded-md bg-[rgb(21_151_255)] px-5 py-1.5 text-sm font-semibold text-white transition hover:bg-[rgb(13_122_204)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      <ConfirmDialog
        isOpen={pendingLargeFile != null}
        title="Large File"
        message={pendingLargeFile?.message ?? ''}
        primaryLabel="Yes"
        onPrimary={handleConfirmLargeFile}
        secondaryLabel="No"
        onSecondary={handleCancelLargeFile}
        onDismiss={handleCancelLargeFile}
        messageLayout="largeFile"
        primaryFirst
        actionStyle="application"
        sectionedChrome
      />
    </>
  )
}
